import type { CSSProperties, FormEvent } from 'react'
import type { EnergyType } from 'types'
import type { OfferInputName, Offer } from 'types/seller'

import { useState, useEffect } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import InputMask from 'react-input-mask'

import { useStore } from 'store'
import { ENERGY_TYPE, fiveYearsFromNow } from 'common'
import {
  addParamToPath,
  dimmBackground,
  getInput,
  getValue,
  leaveOnlyNumbers,
  trimFormData
} from 'utils'
import copy from 'copy.json'
import { adminPanelPaths, basePaths, clientPanelPaths } from 'paths'
import { addOfferFormAutoFill } from 'utils/devTools'
import { validateOfferForm } from 'utils/pageValidators'

import useIsClientPage from 'hooks/useIsClientPage'

import Button from 'components/Button'
import Input, { PrefixedInput } from 'components/Input'
import InputLabel from 'components/InputLabel'
import Radio, { ActiveRadioLabel, CombinedRadios } from 'components/Radio'
import Spinner from 'components/Spinner'
import AddResourceContent from 'components/AddResourceContent'

import { SunIcon, WindIcon } from 'icons'

export default function AddPlannedOfferScreen() {
  const { api, currentUser, fieldErrors } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isClientPage = useIsClientPage()

  const [energyType, setEnergyType] = useState<EnergyType>('SOLAR')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ [key in OfferInputName]?: string }>({})

  const sellerId = location.state.sellerId

  const validateInput = (
    event?: FormEvent<HTMLInputElement>,
    element?: HTMLInputElement | null
  ) => {
    const input = element || event?.currentTarget

    if (!input) return

    const validation = validateOfferForm(input)

    setErrors(state => ({ ...state, [input.name]: validation }))

    return validation
  }

  const getCity = async (zipCode: string) => {
    const input: HTMLInputElement | null = document.querySelector('[name="location_city"]')
    const city = await api.getCity(zipCode, isClientPage)

    if (!input || !city) return

    input.value = city
    setErrors(state => ({ ...state, location_city: undefined }))
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (loading || !(currentUser || isClientPage)) return

    const form = event.currentTarget
    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateOfferForm(input) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) {
      setErrors(errors)
      return
    }

    setLoading(true)

    const requestData: Partial<Offer> = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({ ...result, [name]: value === '' ? null : value }),
      {}
    )

    if (currentUser) {
      requestData.seller_id = currentUser.id
    }

    requestData.installation_power = parseFloat(String(requestData.installation_power))
    requestData.minimal_contract_length = parseFloat(String(requestData.minimal_contract_length))
    requestData.maximal_contract_length = parseFloat(String(requestData.maximal_contract_length))
    requestData.energy_price = parseFloat(String(requestData.energy_price))
    requestData.location_zip_code = leaveOnlyNumbers(requestData.location_zip_code)

    const response = await api.addPlannedOffer(isClientPage, JSON.stringify(requestData))

    setLoading(false)

    if (response && sellerId) {
      navigate(addParamToPath(adminPanelPaths.seller.search, sellerId))
      return
    }

    if (response && isClientPage) {
      navigate(clientPanelPaths.confirmation)
      return
    }

    if (response) navigate(adminPanelPaths.seller.search)
  }

  useEffect(dimmBackground)

  useEffect(() => {
    setErrors(fieldErrors)
    getInput(Object.keys(fieldErrors)[0])?.scrollIntoView({ behavior: 'smooth' })
  }, [fieldErrors])

  if (!currentUser && !isClientPage) return <Navigate to={basePaths.admin_panel} />

  return (
    <AddResourceContent as="form" noValidate onSubmit={submit}>
      {process.env.NODE_ENV === 'development' && (
        <button type="button" onClick={addOfferFormAutoFill}>
          Auto fill
        </button>
      )}

      <h1>{copy.seller.add_offer.heading}</h1>
      <p>{copy.seller.add_offer.paragraph}</p>

      <CombinedRadios>
        <span>{copy.common.form.energy_type}</span>

        <div>
          <ActiveRadioLabel active={energyType === 'SOLAR'} hiddenRadio style={{ padding: 10 }}>
            <Radio
              name="energy_type"
              value="SOLAR"
              checked={energyType === 'SOLAR'}
              onChange={() => setEnergyType('SOLAR')}
            />
            <SunIcon /> {ENERGY_TYPE.SOLAR}
          </ActiveRadioLabel>

          <ActiveRadioLabel active={energyType === 'WIND'} hiddenRadio style={{ padding: 10 }}>
            <Radio
              name="energy_type"
              value="WIND"
              checked={energyType === 'WIND'}
              onChange={() => setEnergyType('WIND')}
            />
            <WindIcon /> {ENERGY_TYPE.WIND}
          </ActiveRadioLabel>
        </div>
      </CombinedRadios>

      <ElectricEnergyMeterNumberWrapper>
        <span>{copy.common.form.electric_energy_meter_number}</span>
        <p>{copy.seller.add_offer.paragraph_under_construction}</p>

        <Link
          to={
            isClientPage
              ? clientPanelPaths.additional_information
              : adminPanelPaths.seller.additional_information
          }
        >
          {copy.seller.add_offer.link}
        </Link>
      </ElectricEnergyMeterNumberWrapper>

      <InputLabel>
        <span>{copy.common.form.building_permit_number}</span>
        <span>{errors.building_permit_number}</span>
        <Input name="building_permit_number" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span>{copy.common.form.installation_power}</span>
        <span>{errors.installation_power}</span>
        <PrefixedInput
          name="installation_power"
          type="number"
          prefix={copy.common.MW}
          prefixWidth="50px"
          reverse
          step={0.01}
          min={0}
          max={1000000}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{copy.common.form.energy_price}</span>
        <span>{errors.energy_price}</span>
        <PrefixedInput
          name="energy_price"
          prefix={copy.common.zl}
          prefixWidth="28px"
          reverse
          type="number"
          step={0.01}
          min={1}
          max={10000}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{copy.seller.add_offer.form.production_start_date}</span>
        <span>{errors.production_start_date}</span>
        <Input
          type="date"
          name="production_start_date"
          min="1970-01-01"
          max={fiveYearsFromNow}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <Subheader>{copy.seller.add_offer.subheading_1}</Subheader>

      <InputLabel>
        <span>{copy.common.form.location_zip_code}</span>
        <span>{errors.location_zip_code}</span>
        <InputMask
          mask="99-999"
          maskPlaceholder={null}
          onInput={event => !validateInput(event) && getCity(event.currentTarget.value)}
          onBlur={validateInput}
        >
          <Input name="location_zip_code" style={{ width: '50%' }} />
        </InputMask>
      </InputLabel>

      <InputLabel>
        <span>{copy.common.form.location_city}</span>
        <span>{errors.location_city}</span>
        <Input name="location_city" onBlur={validateInput} />
      </InputLabel>

      <Subheader>{copy.seller.add_offer.subheading_2}</Subheader>

      <div style={{ display: 'flex' }}>
        <InputLabel style={{ width: '100%' }}>
          <span>{copy.seller.add_offer.form.minimal_contract_length}</span>
          <span style={{ '--errorMessageHeight': '45px' } as CSSProperties}>
            {errors.minimal_contract_length}
          </span>
          <PrefixedInput
            name="minimal_contract_length"
            type="number"
            prefix={copy.common.months_abbr}
            prefixWidth="52px"
            reverse
            min={1}
            max={240}
            onBlur={event => {
              validateInput(event)
              getValue('maximal_contract_length') &&
                validateInput(undefined, getInput('maximal_contract_length'))
            }}
          />
        </InputLabel>

        <InputLabel style={{ width: '100%' }}>
          <span>{copy.seller.add_offer.form.maximal_contract_length}</span>
          <span style={{ '--errorMessageHeight': '45px' } as CSSProperties}>
            {errors.maximal_contract_length}
          </span>
          <PrefixedInput
            name="maximal_contract_length"
            type="number"
            prefix={copy.common.months_abbr}
            prefixWidth="52px"
            reverse
            min={1}
            max={240}
            onBlur={event => {
              validateInput(event)
              getValue('minimal_contract_length') &&
                validateInput(undefined, getInput('minimal_contract_length'))
            }}
          />
        </InputLabel>
      </div>

      <Button type="submit">
        {loading ? <Spinner size={16} /> : copy.seller.add_offer.button}
      </Button>

      <Button secondary onClick={() => navigate(-1)} type="button">
        {copy.common.button.go_back}
      </Button>
    </AddResourceContent>
  )
}

const ElectricEnergyMeterNumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  > span {
    color: var(--gray);
  }

  > a {
    text-decoration: none;
    color: var(--green);
  }

  > p {
    font: var(--600-13);
  }
`

const Subheader = styled.h4`
  font: var(--600-16);
`
